package it.neckar.lizergy.model.configuration.components

import it.neckar.lizergy.model.ElementsSelectionEntry
import it.neckar.lizergy.model.configuration.components.BatteryConfiguration.BatteryConfigurationId
import it.neckar.lizergy.model.configuration.components.FacilityConfiguration.FacilityConfigurationId
import it.neckar.lizergy.model.configuration.moduleLayout.ModuleLayout.ModuleLayoutId
import it.neckar.lizergy.model.configuration.quote.builder.BasicBatteryInverter
import it.neckar.lizergy.model.configuration.quote.builder.HeaterRod
import it.neckar.lizergy.model.configuration.quote.builder.HybridInverter
import it.neckar.lizergy.model.configuration.quote.builder.InverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.InverterSelection
import it.neckar.lizergy.model.configuration.quote.builder.InverterType
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterSelection
import it.neckar.lizergy.model.financial.quote.ConfiguredOptionality
import it.neckar.open.formatting.format
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.open.kotlin.lang.fastMap
import it.neckar.open.unit.si.m
import it.neckar.uuid.HasUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.ValueClassUuid
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlin.uuid.Uuid

interface FacilityConfiguration : HasUuid {
  val id: FacilityConfigurationId
  val numberOfOptimizers: Int
  val kabelwegZugschlagLength: @m Double
  val numberUeberspannungsSchutz: Int?

  val inverterSelection: InverterSelection
  val inverterConfigurations: List<InverterConfiguration>?
  val batteryConfigurationId: BatteryConfigurationId?

  val facilityWorkEffort: ConfigurationItem

  val waermepumpenanbindung: Boolean
  val erdspiessSetzen: Boolean
  val onlineMonitoring: ConfiguredOptionality
  val integrateInverterIntoNetwork: ConfiguredOptionality

  override val uuid: Uuid
    get() = id.uuid


  @Serializable
  data class FacilityConfigurationId(override val uuid: NativeUuid) : ValueClassUuid {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): FacilityConfigurationId {
        return FacilityConfigurationId(randomUuid4())
      }
    }
  }

}

@Serializable
data class ResolvedFacilityConfiguration(
  override val id: FacilityConfigurationId,
  override val numberOfOptimizers: Int,
  override val kabelwegZugschlagLength: @m Double,
  override val numberUeberspannungsSchutz: Int?,

  override val inverterSelection: ResolvedInverterSelection,
  override val inverterConfigurations: List<ResolvedInverterConfiguration>,
  val batteryConfiguration: BatteryConfiguration?,

  override val facilityWorkEffort: ConfigurationItem,

  override val waermepumpenanbindung: Boolean,
  override val erdspiessSetzen: Boolean,
  override val onlineMonitoring: ConfiguredOptionality,
  override val integrateInverterIntoNetwork: ConfiguredOptionality,
  val heaterRod: HeaterRod?,
) : FacilityConfiguration {

  override val batteryConfigurationId: BatteryConfigurationId?
    get() = batteryConfiguration?.id

  val inverterSelectionWithBatteryInverter: List<ElementsSelectionEntry<InverterType>>
    get() = when (val batteryInverter = batteryConfiguration?.inverterType) {
      is BasicBatteryInverter, is HybridInverter -> inverterSelection.entriesNonEmpty + ElementsSelectionEntry(batteryInverter, 1)
      null -> inverterSelection.entriesNonEmpty
    }

  val inverterSelectionWithHybridInverter: List<ElementsSelectionEntry<InverterType>>
    get() = when (val batteryInverter = batteryConfiguration?.inverterType) {
      is HybridInverter -> inverterSelection.entriesNonEmpty + ElementsSelectionEntry(batteryInverter, 1)
      is BasicBatteryInverter, null -> inverterSelection.entriesNonEmpty
    }

  val numberOfStrings: Int
    get() = inverterConfigurations.sumOf { it.numberOfStrings }

  val ueberSpannungsSchutzCount: Int
    get() = numberUeberspannungsSchutz ?: numberOfStrings

  val numberOfOptimalModules: Int
    get() = inverterConfigurations.sumOf { it.numberOfOptimalModules }

  val numberOfPlannedModules: Int
    get() = inverterConfigurations.sumOf { it.relevantMpptConfigurations.sumOf { it.relevantStringConfigurations.sumOf { it.totalModuleCount } } }

  fun forTheseLayouts(moduleLayouts: List<ModuleLayoutId>): ResolvedFacilityConfiguration {
    return copy(inverterConfigurations = inverterConfigurations.map { it.forTheseLayouts(moduleLayouts) }.sortedBy { it.inverter.capacity })
  }

  /**
   * Kept configurations stay in their relative order, because `InverterMessdaten` stored without `InverterConfigurationId` belong to the inverter at their list position.
   */
  fun recalculateInverterConfigurations(): ResolvedFacilityConfiguration {
    val emptyInverterConfigurations = emptyInverterConfigurationsFor(inverterSelection, batteryConfiguration)
    val recalculatedIds = emptyInverterConfigurations.map { it.id }.toSet()
    val currentIds = inverterConfigurations.map { it.id }.toSet()
    return copy(
      inverterConfigurations = inverterConfigurations.filter { it.id in recalculatedIds } + emptyInverterConfigurations.filter { (it.id in currentIds).not() },
    )
  }

  fun invalidManagerCombination(electricityWorkConfiguration: ResolvedElectricityWorkConfiguration): Boolean? {
    return electricityWorkConfiguration.invalidManagerCombination(inverterSelection, batteryConfiguration?.inverterType)
  }

  fun allModuleStringConfigurations(): List<InverterConfiguration.StringConfiguration> {
    return inverterConfigurations.flatMap { it.relevantMpptConfigurations.flatMap { it.relevantStringConfigurations } }
  }


  fun duplicate(mapOfOldToNewUuids: MutableMap<Uuid, Uuid>): ResolvedFacilityConfiguration {
    val newId = FacilityConfigurationId.random()
    mapOfOldToNewUuids[id.uuid] = newId.uuid
    return copy(
      id = newId,
      inverterSelection = inverterSelection.duplicate(mapOfOldToNewUuids),
    )
  }

  companion object {
    operator fun invoke(
      id: FacilityConfigurationId,
      numberOfOptimizers: Int,
      kabelwegZugschlagLength: @m Double,
      numberUeberspannungsSchutz: Int?,
      inverterSelection: ResolvedInverterSelection,
      batteryConfiguration: BatteryConfiguration?,
      facilityWorkEffort: ConfigurationItem,
      waermepumpenanbindung: Boolean,
      erdspiessSetzen: Boolean,
      onlineMonitoring: ConfiguredOptionality,
      integrateInverterIntoNetwork: ConfiguredOptionality,
      heaterRod: HeaterRod?,
    ): ResolvedFacilityConfiguration {
      return ResolvedFacilityConfiguration(
        id = id,
        numberOfOptimizers = numberOfOptimizers,
        kabelwegZugschlagLength = kabelwegZugschlagLength,
        numberUeberspannungsSchutz = numberUeberspannungsSchutz,
        inverterSelection = inverterSelection,
        inverterConfigurations = emptyInverterConfigurationsFor(inverterSelection, batteryConfiguration),
        batteryConfiguration = batteryConfiguration,
        facilityWorkEffort = facilityWorkEffort,
        waermepumpenanbindung = waermepumpenanbindung,
        erdspiessSetzen = erdspiessSetzen,
        onlineMonitoring = onlineMonitoring,
        integrateInverterIntoNetwork = integrateInverterIntoNetwork,
        heaterRod = heaterRod,
      )
    }

    private fun emptyInverterConfigurationsFor(inverterSelection: ResolvedInverterSelection, batteryConfiguration: BatteryConfiguration?): List<ResolvedInverterConfiguration> {
      val inverterSelections = when (val batteryInverter = batteryConfiguration?.inverterType) {
        is HybridInverter -> inverterSelection.entriesNonEmpty + ElementsSelectionEntry(batteryInverter, 1)
        is BasicBatteryInverter, null -> inverterSelection.entriesNonEmpty
      }
      return inverterSelections.flatMap { selectionEntry ->
        selectionEntry.amount.fastMap { inverterIndex -> ResolvedInverterConfiguration(selectionEntry.element, inverterIndex) }
      }
    }

    fun getEmpty(id: FacilityConfigurationId = FacilityConfigurationId.random()): ResolvedFacilityConfiguration {
      return ResolvedFacilityConfiguration(
        id = id,
        numberOfOptimizers = 0,
        kabelwegZugschlagLength = 0.0,
        numberUeberspannungsSchutz = null,
        inverterSelection = ResolvedInverterSelection.getEmpty(),
        batteryConfiguration = null,
        facilityWorkEffort = ConfigurationItem.getRisikoZuschlag(),
        waermepumpenanbindung = false,
        erdspiessSetzen = false,
        onlineMonitoring = ConfiguredOptionality.Selected,
        integrateInverterIntoNetwork = ConfiguredOptionality.NotSelected,
        heaterRod = null,
      )
    }

    fun presentationDemo(id: FacilityConfigurationId = FacilityConfigurationId.random(), inverterSelection: ResolvedInverterSelection, batteryConfiguration: BatteryConfiguration?): ResolvedFacilityConfiguration {
      return ResolvedFacilityConfiguration(
        id = id,
        numberOfOptimizers = 0,
        kabelwegZugschlagLength = 15.0,
        numberUeberspannungsSchutz = 1,
        batteryConfiguration = batteryConfiguration,
        inverterSelection = inverterSelection,
        facilityWorkEffort = ConfigurationItem.getRisikoZuschlag(),
        waermepumpenanbindung = false,
        erdspiessSetzen = false,
        onlineMonitoring = ConfiguredOptionality.Selected,
        integrateInverterIntoNetwork = ConfiguredOptionality.NotSelected,
        heaterRod = null,
      )
    }
  }
}

fun List<ElementsSelectionEntry<InverterType>>.format(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, entrySeparator: String = ", ", whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  val entriesNonEmpty = filter { it.amount > 0 }
  if (entriesNonEmpty.isEmpty()) return "-"
  return entriesNonEmpty.joinToString(entrySeparator) {
    "${it.amount.format(i18nConfiguration, whitespaceConfig)} × ${it.element.format()}"
  }
}

fun List<ElementsSelectionEntry<InverterType>>.formatDescription(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, entrySeparator: String = ", ", whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  val entriesNonEmpty = filter { it.amount > 0 }
  if (entriesNonEmpty.isEmpty()) return "-"
  return entriesNonEmpty.joinToString(entrySeparator) {
    "${it.amount.format(i18nConfiguration, whitespaceConfig)} × ${it.element.description}"
  }
}
